import { Link, usePage } from "@inertiajs/react";
import { BookOpen, CircleHelp, Users } from "lucide-react";
import type * as React from "react";
import { AccountNavigation } from "@/bizminer/layouts/app/sidebar/AccountNavigation.tsx";
import { CreditsWidget } from "@/bizminer/layouts/app/sidebar/credits-widget.tsx";
import { DashboardMenuItem } from "@/bizminer/layouts/app/sidebar/DashboardMenuItem.tsx";
import { NavUser } from "@/bizminer/layouts/app/sidebar/nav-user.tsx";
import { OrganizationWithLogo } from "@/bizminer/layouts/app/sidebar/OrganizationWithLogo.tsx";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar.tsx";
import {
  bizminer_app_contacts_path,
  bizminer_app_faq_path,
  bizminer_app_product_guides_path,
} from "@/routes";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";

const Version = ({ version }: { version: string }) => {
  return (
    <span className="text-muted-foreground text-xs">
      <span className="font-medium">Vitelis </span>
      <span>{version}</span>
    </span>
  );
};

interface BizminerSidebarProps extends React.ComponentProps<typeof Sidebar> {
  hasAccountContext: boolean;
  isAccountPage: boolean;
}

export function BizminerSidebar({
  hasAccountContext,
  isAccountPage,
  ...props
}: BizminerSidebarProps) {
  const page = usePage<BizminerInertiaSharedProps>();
  const component = page.component;
  const sidebar = page.props.sidebar;
  const permissions = page.props.permissions ?? {
    can_view_credits: false,
    can_request_analysis: false,
    can_manage_accounts: false,
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <OrganizationWithLogo organization={sidebar?.organization} />
      </SidebarHeader>

      <SidebarContent>
        {/* Organization-level navigation */}
        <DashboardMenuItem />

        {/* Account-level navigation (conditional) */}
        {hasAccountContext && isAccountPage && (
          <AccountNavigation isAccountPage={isAccountPage} />
        )}

      </SidebarContent>

      <SidebarFooter>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={component.startsWith("Bizminer/App/ProductGuide/")}
              >
                <Link
                  href={bizminer_app_product_guides_path(undefined, {
                    account_id: undefined,
                  })}
                >
                  <BookOpen />
                  <span>Product Guide</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupLabel className="sr-only">Help & Support</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={component === "Bizminer/App/Pages/Faq"}
              >
                <Link
                  href={bizminer_app_faq_path(undefined, {
                    account_id: undefined,
                  })}
                >
                  <CircleHelp />
                  <span>FAQ</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={component === "Bizminer/App/Pages/Contacts"}
              >
                <Link
                  href={bizminer_app_contacts_path(undefined, {
                    account_id: undefined,
                  })}
                >
                  <Users />
                  <span>Contacts</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <SidebarSeparator />
        {permissions.can_view_credits && typeof sidebar?.credits === "number" && (
          <CreditsWidget credits={sidebar.credits} />
        )}
        {sidebar?.user ? <NavUser user={sidebar.user} /> : null}
        {sidebar?.version ? <Version version={sidebar.version} /> : null}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
